import React from 'react';
import { 
  Box,
  Button, 
  Typography, 
  Slider, 
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
// Import the constants
import { graphVizBehaviourOptions } from './GraphVizConstants';
import { useTranslation } from 'react-i18next';

// Get the default values from constants
const DEFAULT_PHYSICS = graphVizBehaviourOptions.physics.forceAtlas2Based;

export interface PhysicsParams {
  gravitationalConstant: number;
  centralGravity: number;
  springLength: number;
  springConstant: number;
  damping: number;
}

interface PhysicsSettingsProps {
  settings: PhysicsParams;
  onSettingsChange: (newSettings: PhysicsParams) => void;
}

const PhysicsSettings: React.FC<PhysicsSettingsProps> = ({ 
  settings, 
  onSettingsChange,
}) => {
  const { t } = useTranslation();

  const handleChange = (param: keyof PhysicsParams) => (
    _event: Event, 
    value: number | number[]
  ) => {
    const newValue = typeof value === 'number' ? value : value[0];
    const newSettings = { ...settings, [param]: newValue };
    onSettingsChange(newSettings);
  };

  const resetToDefaults = () => {
    // Use the imported defaults
    onSettingsChange(DEFAULT_PHYSICS);
  };

  return (
    <Box sx={{ p: 1, width: 250 }}>
        <Typography variant="subtitle2" sx={{p: 1}}>{t('graph.physics.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <Typography variant="caption" gutterBottom>
            {t('graph.physics.gravitationalConstant.name')}: {settings.gravitationalConstant}
            </Typography>
            <Tooltip 
            title={
                <React.Fragment>
                <Typography variant="subtitle2">{t('graph.physics.gravitationalConstant.name')}</Typography>
                <Typography variant="body2">{t('graph.physics.gravitationalConstant.description')}</Typography>
                </React.Fragment>
            } 
            arrow
            placement="top"
            >
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
        </Box>
        <Box sx={{px: 1}}>
            <Slider
                value={settings.gravitationalConstant}
                onChange={handleChange('gravitationalConstant')}
                min={-750}
                max={0}
                step={10}
                marks={[{ value: DEFAULT_PHYSICS.gravitationalConstant, label: t('graph.default') }]}
                size="small"
                valueLabelDisplay="auto"
                sx={{
                '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    color: 'text.primary',
                }
                }}
            />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 1 }}>
            <Typography variant="caption" gutterBottom>
            {t('graph.physics.centralGravity.name')}: {settings.centralGravity.toFixed(3)}
            </Typography>
            <Tooltip 
            title={
                <React.Fragment>
                <Typography variant="subtitle2">{t('graph.physics.centralGravity.name')}</Typography>
                <Typography variant="body2">{t('graph.physics.centralGravity.description')}</Typography>
                </React.Fragment>
            } 
            arrow
            placement="top"
            >
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
        </Box>
        <Box sx={{px: 1}}>
            <Slider
                value={settings.centralGravity}
                onChange={handleChange('centralGravity')}
                min={0.0}
                max={0.2}
                step={0.001}
                marks={[{ value: DEFAULT_PHYSICS.centralGravity, label: t('graph.default') }]}
                size="small"
                valueLabelDisplay="auto"
                sx={{
                '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    color: 'text.primary',
                }
                }}
            />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 1 }}>
            <Typography variant="caption" gutterBottom>
            {t('graph.physics.springLength.name')}: {settings.springLength}
            </Typography>
            <Tooltip 
            title={
                <React.Fragment>
                <Typography variant="subtitle2">{t('graph.physics.springLength.name')}</Typography>
                <Typography variant="body2">{t('graph.physics.springLength.description')}</Typography>
                </React.Fragment>
            } 
            arrow
            placement="top"
            >
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
        </Box>
        <Box sx={{px: 1}}>
            <Slider
                value={settings.springLength}
                onChange={handleChange('springLength')}
                min={50}
                max={300}
                step={10}
                marks={[{ value: DEFAULT_PHYSICS.springLength, label: t('graph.default') }]}
                size="small"
                valueLabelDisplay="auto"
                sx={{
                '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    color: 'text.primary',
                }
                }}
            />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 1 }}>
            <Typography variant="caption" gutterBottom>
            {t('graph.physics.springConstant.name')}: {settings.springConstant.toFixed(2)}
            </Typography>
            <Tooltip 
            title={
                <React.Fragment>
                <Typography variant="subtitle2">{t('graph.physics.springConstant.name')}</Typography>
                <Typography variant="body2">{t('graph.physics.springConstant.description')}</Typography>
                </React.Fragment>
            } 
            arrow
            placement="top"
            >
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
        </Box>
        <Box sx={{px: 1}}>
            <Slider
                value={settings.springConstant}
                onChange={handleChange('springConstant')}
                min={0}
                max={1}
                step={0.05}
                marks={[{ value: DEFAULT_PHYSICS.springConstant, label: t('graph.default') }]}
                size="small"
                valueLabelDisplay="auto"
                sx={{
                '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    color: 'text.primary',
                }
                }}
            />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 1 }}>
            <Typography variant="caption" gutterBottom>
            {t('graph.physics.damping.name')}: {settings.damping.toFixed(2)}
            </Typography>
            <Tooltip 
            title={
                <React.Fragment>
                <Typography variant="subtitle2">{t('graph.physics.damping.name')}</Typography>
                <Typography variant="body2">{t('graph.physics.damping.description')}</Typography>
                </React.Fragment>
            } 
            arrow
            placement="top"
            >
            <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
        </Box>
        <Box sx={{px: 1}}>
            <Slider
                value={settings.damping}
                onChange={handleChange('damping')}
                min={0}
                max={1}
                step={0.05}
                marks={[{ value: DEFAULT_PHYSICS.damping, label: t('graph.default') }]}
                size="small"
                valueLabelDisplay="auto"
                sx={{
                '& .MuiSlider-markLabel': {
                    fontSize: '0.6rem',
                    color: 'text.primary',
                }
                }}
            />
        </Box>

        <Box sx={{px: 1, mt: 1}}>
            <Button 
                variant="outlined" 
                onClick={resetToDefaults}
                sx={{ mt: 1 }}
                fullWidth
                size="small"
            >
                {t('graph.physics.reset')}
            </Button>
        </Box>
    </Box>
  );
};

export default PhysicsSettings;
